import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { IoClose, IoCloseOutline } from "react-icons/io5";
import { MdOutlineLocalOffer, MdOutlineDiscount } from "react-icons/md";
import { FiChevronLeft } from "react-icons/fi";
import useIsDesktop from "@/hooks/useIsDesktop";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import { useAlert } from "@/Common/CustomAlert/AlertContext";
import { sendAuthLogin, verifyAuthOtp } from "@/services/api/auth";
import { getUserDetails } from "@/services/api/auth";
import {
  buildAuthRequestPayload,
  getAuthErrorMessage,
  isAuthSuccess,
  parseAuthSession,
  parseCredentialInput,
} from "@/utils/authHelpers";
import { normalizeUserDetails, setAuthSession, withRegionPath } from "@/utils/session";
import PhoneInputField from "@/Common/PhoneInputField";
import {
  getDefaultPhoneCountry,
  getNationalDigitsFromPhoneValue,
  toE164FromLocalDigits,
} from "@/utils/phoneInput";
import styles from "./LoginSignupModal.module.css";

const PHONE_SWITCH_DIGITS = 6;

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

function PromoPanel() {
  return (
    <div className={styles.promoPanel}>
      <Image
        src="/images/login-image.png"
        alt=""
        fill
        className={styles.promoImage}
        quality={70}
        loading="lazy"
      />
      <div className={styles.promoOverlay} />
      <div className={styles.promoContent}>
        <h2 className={styles.promoTitle}>Exclusive offers, just for you</h2>

        <div className={styles.promoFeature}>
          <span className={styles.promoIconWrap}>
            <img src="/images/login-price.svg" />
          </span>
          <div>
            <p className={styles.promoFeatureTitle}>Best prices</p>
            <p className={styles.promoFeatureText}>
              Enjoy top rates on flights, hotels, and experiences, every time.
            </p>
          </div>
        </div>

        <div className={styles.promoFeature}>
          <span className={styles.promoIconWrap}>
            <img src="/images/login-discount.svg" />
          </span>
          <div>
            <p className={styles.promoFeatureTitle}>Special discounts</p>
            <p className={styles.promoFeatureText}>
              Seasonal deals and exclusive travel promotions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function OtpInputs({ value, onChange, disabled = false }) {
  const inputsRef = useRef([]);

  const digits = useMemo(() => {
    const chars = value.split("").slice(0, OTP_LENGTH);
    while (chars.length < OTP_LENGTH) chars.push("");
    return chars;
  }, [value]);

  const focusInput = (index) => {
    inputsRef.current[index]?.focus();
  };

  const handleChange = (index, nextValue) => {
    const digit = nextValue.replace(/\D/g, "").slice(-1);
    const nextDigits = [...digits];
    nextDigits[index] = digit;
    onChange(nextDigits.join("").slice(0, OTP_LENGTH));
    if (digit && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    onChange(pasted);
    focusInput(Math.min(pasted.length, OTP_LENGTH - 1));
  };

  return (
    <div className={styles.otpGroup}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(node) => {
            inputsRef.current[index] = node;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          disabled={disabled}
          className={`${styles.otpInput} ${digit ? styles.otpInputFilled : ""}`}
          aria-label={`OTP digit ${index + 1}`}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
}

export default function LoginSignupModal({
  open,
  mode = "login",
  onClose,
  onSuccess,
}) {
  const { isDesktop } = useIsDesktop(900);
  const { showAlert } = useAlert();

  const [step, setStep] = useState("credential");
  const [credential, setCredential] = useState("");
  const [credentialError, setCredentialError] = useState("");
  const [parsedCredentials, setParsedCredentials] = useState(null);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [phoneCountry, setPhoneCountry] = useState(getDefaultPhoneCountry);
  const textInputRef = useRef(null);
  const wasPhoneInputRef = useRef(false);

  const resetState = useCallback(() => {
    setStep("credential");
    setCredential("");
    setCredentialError("");
    setParsedCredentials(null);
    setOtp("");
    setLoading(false);
    setResendTimer(0);
    setPhoneCountry(getDefaultPhoneCountry());
  }, []);

  useBodyScrollLock(open);

  useEffect(() => {
    if (open) return undefined;
    resetState();
    return undefined;
  }, [open, resetState]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || resendTimer <= 0) return undefined;

    const timer = setInterval(() => {
      setResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [open, resendTimer]);

  const parsedInput = useMemo(() => parseCredentialInput(credential), [credential]);
  const canContinue = parsedInput.valid;
  const isEmailInput = credential.includes("@");
  const nationalNumber = useMemo(() => {
    if (isEmailInput) return "";
    return getNationalDigitsFromPhoneValue(credential, phoneCountry).national;
  }, [credential, phoneCountry, isEmailInput]);
  const usePhoneInput =
    !isEmailInput &&
    credential.startsWith("+") &&
    nationalNumber.length >= PHONE_SWITCH_DIGITS;

  useEffect(() => {
    if (usePhoneInput) {
      wasPhoneInputRef.current = true;
      const frame = window.requestAnimationFrame(() => {
        document
          .querySelector(`.${styles.authPhoneField} .PhoneInputInput`)
          ?.focus();
      });
      return () => window.cancelAnimationFrame(frame);
    }

    if (!wasPhoneInputRef.current) return undefined;

    const input = textInputRef.current;
    wasPhoneInputRef.current = false;
    if (!input) return undefined;

    const frame = window.requestAnimationFrame(() => {
      input.focus();
      const cursor = input.value.length;
      input.setSelectionRange(cursor, cursor);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [usePhoneInput]);

  const handleTextChange = (nextValue) => {
    setCredentialError("");
    const next = nextValue ?? "";

    if (next.includes("@")) {
      setCredential(next);
      return;
    }

    const digits = next.replace(/\D/g, "");
    if (digits.length >= PHONE_SWITCH_DIGITS) {
      setCredential(toE164FromLocalDigits(digits, phoneCountry));
      return;
    }

    setCredential(next);
  };

  const handlePhoneChange = (nextValue) => {
    setCredentialError("");
    const next = nextValue ?? "";

    if (!next) {
      setCredential("");
      return;
    }

    const { national } = getNationalDigitsFromPhoneValue(next, phoneCountry);
    if (national.length < PHONE_SWITCH_DIGITS) {
      setCredential(national);
      return;
    }

    setCredential(next);
  };

  const handlePhoneKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleContinue();
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key.length !== 1) return;
    if (/[0-9]/.test(event.key)) return;

    event.preventDefault();
    setCredentialError("");
    setCredential(`${nationalNumber}${event.key}`);
  };

  const otpChannelText = parsedCredentials?.emailId
    ? "Email"
    : parsedCredentials?.channelLabel || "Phone";

  const handleContinue = async () => {
    if (!parsedInput.valid) {
      setCredentialError(parsedInput.error);
      return;
    }

    setCredentialError("");
    setLoading(true);

    try {
      const payload = buildAuthRequestPayload(parsedInput);
      const response = await sendAuthLogin(payload);

      if (!isAuthSuccess(response)) {
        throw new Error(response?.message || "Unable to send OTP.");
      }

      setParsedCredentials(parsedInput);
      setStep("otp");
      setOtp("");
      setResendTimer(RESEND_SECONDS);
    } catch (error) {
      showAlert({
        title: "Login failed",
        message: getAuthErrorMessage(error, "Unable to send OTP."),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!parsedCredentials || resendTimer > 0 || loading) return;

    setLoading(true);
    try {
      const payload = buildAuthRequestPayload(parsedCredentials);
      const response = await sendAuthLogin(payload);

      if (!isAuthSuccess(response)) {
        throw new Error(response?.message || "Unable to resend OTP.");
      }

      setResendTimer(RESEND_SECONDS);
      showAlert({
        title: "OTP sent",
        message: response?.message || "A new OTP has been sent.",
        type: "success"
      });
    } catch (error) {
      showAlert({
        title: "Resend failed",
        message: getAuthErrorMessage(error, "Unable to resend OTP."),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== OTP_LENGTH || !parsedCredentials) return;

    setLoading(true);
    try {
      const payload = {
        ...buildAuthRequestPayload(parsedCredentials),
        otp,
      };
      const response = await verifyAuthOtp(payload);

      if (!isAuthSuccess(response)) {
        throw new Error(response?.message || "Invalid OTP.");
      }

      const session = parseAuthSession(response);
      if (!session.token) {
        throw new Error("Login successful but token was not returned.");
      }

      let userDetails = normalizeUserDetails(response?.data);

      try {
        const profileResponse = await getUserDetails(session.token);
        const profile = Array.isArray(profileResponse?.data)
          ? profileResponse.data[0]
          : profileResponse?.data;

        if (!session.userId) {
          session.userId = String(
            profile?.userId || profile?.UserId || profile?.id || ""
          );
        }

        userDetails = normalizeUserDetails(profile) || userDetails;
      } catch {
        // Profile fetch is best-effort; token is enough for some flows.
      }

      setAuthSession({ ...session, userDetails });
      onSuccess?.();
    } catch (error) {
      showAlert({
        title: "Verification failed",
        message: getAuthErrorMessage(error, "Invalid OTP. Please try again."),
      });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const modalContent = (
    <div
      className={`${styles.root} ${isDesktop ? styles.rootDesktop : styles.rootMobile}`}
      role="presentation"
    >
      <button
        type="button"
        className={styles.overlay}
        aria-label="Close login modal"
        onClick={onClose}
      />

      <div
        className={`${styles.modal} ${isDesktop ? styles.modalDesktop : styles.modalMobile}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
      >
        {isDesktop ? <PromoPanel /> : null}

        <div className={styles.formPanel}>

          <div className={styles.formTopBar}>
            {step === "otp" ? (
              <button
                type="button"
                className={styles.backBtn}
                onClick={() => {
                  setStep("credential");
                  setOtp("");
                }}
              >
                <FiChevronLeft size={15} />
                Back
              </button>
            ) : (
              <span aria-hidden="true" />
            )}

            <button
              type="button"
              className={styles.closeBtn}
              aria-label="Close"
              onClick={onClose}
            >
              <IoCloseOutline size={25} />
            </button>
          </div>

          {step === "credential" ? (
            <div className={styles.formBody}>
              <h2 id="auth-modal-title" className={`${styles.formTitle} ${styles.formTitleNone}`}>
                Start Your Journey with Next Holidays.
              </h2>
              <h2 id="auth-modal-title" className={styles.formTitle2}>
                <span className={styles.formTitleSpan1}>Join Next Holidays!</span>
                <span className={styles.formTitleSpan2}>Let&apos;s Get Started</span>
              </h2>
              <label className={styles.fieldWrap}>
                <span className={styles.fieldLabel}>
                  {usePhoneInput ? "Phone Number" : "Email ID or Phone Number"}
                </span>
                {usePhoneInput ? (
                  <PhoneInputField
                    value={credential}
                    defaultCountry={phoneCountry}
                    error={Boolean(credentialError)}
                    className={styles.authPhoneField}
                    placeholder="Enter mobile number"
                    keepNationalOnCountryChange
                    onChange={handlePhoneChange}
                    onCountryChange={setPhoneCountry}
                    onKeyDown={handlePhoneKeyDown}
                  />
                ) : (
                  <input
                    ref={textInputRef}
                    type="text"
                    value={credential}
                    placeholder="Your Email ID or Phone Number"
                    className={`${styles.fieldInput} ${credentialError ? styles.fieldInputError : ""}`}
                    onChange={(event) => handleTextChange(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleContinue();
                      }
                    }}
                  />
                )}
              </label>
              {credentialError ? (
                <p className={styles.errorText}>{credentialError}</p>
              ) : null}
              <div className={styles.primaryBtnDiv}>
                <button
                  type="button"
                  className={`${styles.primaryBtn} ${canContinue ? styles.primaryBtnActive : ""}`}
                  disabled={!canContinue || loading}
                  onClick={handleContinue}
                >
                  {loading ? "Please wait..." : "Continue"}
                </button>
              </div>
              <div className={styles.legalTextDiv}>
                <p className={styles.legalText}>
                  By proceeding, you agree to Next Holidays&apos;{" "}
                  <a href={withRegionPath("/privacy-policy")}>Privacy Policy</a>,{" "}
                  <a href={withRegionPath("/termsandcondition")}>User Agreement</a>{" "}
                  and{" "}
                  <a href={withRegionPath("/termsandcondition")}>T&amp;Cs</a>
                </p>
              </div>
            </div>
          ) : (
            <div className={styles.formBody}>
              <h2 id="auth-modal-title" className={`${styles.formTitle} ${styles.formTitleOtp}`}>
                Enter OTP
                <span className={styles.formSubtitle2}>
                  {/* We have sent an OTP to your {otpChannelText}. */}
                  Check your {otpChannelText} for the OTP we sent you.
                </span>
              </h2>
              <p className={styles.formSubtitle}>
                {/* We have sent an OTP to your {otpChannelText}. */}
                Check your {otpChannelText} for the OTP we sent you.
              </p>

              <OtpInputs value={otp} onChange={setOtp} disabled={loading} />

              <p className={styles.resendText}>
                Didn&apos;t receive the OTP?{" "}
                {resendTimer > 0 ? (
                  <span className={styles.resendTimer}>
                    Resend in 00:{String(resendTimer).padStart(2, "0")}
                  </span>
                ) : (
                  <button
                    type="button"
                    className={styles.resendBtn}
                    onClick={handleResend}
                    disabled={loading}
                  >
                    Resend OTP
                  </button>
                )}
              </p>

              <div className={styles.primaryBtnDiv}>
                <button
                  type="button"
                  className={`${styles.primaryBtn} ${otp.length === OTP_LENGTH ? styles.primaryBtnActive : ""}`}
                  disabled={otp.length !== OTP_LENGTH || loading}
                  onClick={handleVerifyOtp}
                >
                  {loading ? "Verifying..." : "Verify OTP"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;

  return createPortal(modalContent, document.body);
}
